import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import {
  Modal,
  ButtonGroup,
  Button,
  Form,
  FormGroup,
  FormControl,
  ControlLabel,
  HelpBlock,
  Alert,
  Col,
  Tabs,
  Tab,
  Checkbox,
} from "react-bootstrap";
import QrReader from "react-qr-reader";
import { Typeahead } from "react-bootstrap-typeahead";
import moment from "moment";
import {
  SESSION_TYPES,
  MAX_CONSECUTIVE_UNCLEANED_BREWS,
} from "@common/constants/sessionConstants";
import { routes } from "@common/constants/urls";
import { isBrewable } from "../../../util/recipeValidation";
import * as sessionService from "@brewery/services/sessionService";
import * as recipeService from "@beers/services/recipeService";
import * as deviceService from "@common/services/deviceService";
import { get as requestGet } from "@common/services/request";
import ButtonWithLoad from "@common/components/ButtonWithLoad/ButtonWithLoad";
import { ACCESS_TYPES } from "@common/constants/accessTypes";
import "./BrewModal.scss";

const scanStates = {
  scanning: 0,
  success: 1,
  error: 2,
};

const BrewModal = ({
  show,
  handleClose,
  selectedDevice: propSelectedDevice,
  selectedRecipe: propSelectedRecipe,
}) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo);

  const [fetchingRecipes, setFetchingRecipes] = useState(false);
  const [fetchingDevices, setFetchingDevices] = useState(false);
  const [fetchingBeers, setFetchingBeers] = useState(false);

  const [recipes, setRecipes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [beers, setBeers] = useState([]);

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [recipeError, setRecipeError] = useState(false);

  const [selectedBeer, setSelectedBeer] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  const [readinessInfo, setReadinessInfo] = useState(null);
  const [skipCleaning, setSkipCleaning] = useState(false);
  const [confirmSkipWarning, setConfirmSkipWarning] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(1);

  const [scanState, setScanState] = useState(scanStates.scanning);
  const [brewticket, setBrewticket] = useState(null);
  const [enterManually, setEnterManually] = useState(false);
  const [manualCode, setManualCode] = useState("");

  const [brewEligible, setBrewEligible] = useState(true);
  const [nextBrewDate, setNextBrewDate] = useState(null);

  const typeaheadVersionsRef = useRef(null);

  const checkBrewEligibility = useCallback(async () => {
    try {
      const response = await requestGet("brew-eligibility/");
      setBrewEligible(response.eligible);
      setNextBrewDate(response.next_brew_date || null);
    } catch {
      // Ignore if not available
    }
  }, []);

  const fetchRecipes = useCallback(
    async (beerID) => {
      if (!beerID) return;
      setFetchingRecipes(true);
      setRecipeError(false);
      try {
        const response = await recipeService.getRecipes(beerID);
        setRecipes(response || []);
        if (propSelectedRecipe && propSelectedRecipe.beer_id === beerID) {
          const matched = response.find((r) => r.id === propSelectedRecipe.id);
          setSelectedRecipe(matched || null);
        } else if (response && response.length > 0) {
          const latest = response.reduce((a, b) =>
            +a.version_name > +b.version_name ? a : b,
          );
          setSelectedRecipe(latest);
        }
      } catch {
        setRecipeError(true);
      } finally {
        setFetchingRecipes(false);
      }
    },
    [propSelectedRecipe],
  );

  const fetchDevices = useCallback(async () => {
    setFetchingDevices(true);
    try {
      const response = await deviceService.getReadyToBrewDevices({
        includeNeedsCleaning: true,
      });
      setDevices(response || []);
      if (propSelectedDevice) {
        const matched = response.find((d) => d.uuid === propSelectedDevice);
        if (matched) setSelectedDevice(matched);
      } else if (response && response.length > 0) {
        // Prioritize a base station that is eligible to brew (consecutive_uncleaned_brews < MAX_CONSECUTIVE_UNCLEANED_BREWS)
        const brewEligibleDevice = response.find(
          (d) =>
            (!d.consecutive_uncleaned_brews ||
              d.consecutive_uncleaned_brews <
                MAX_CONSECUTIVE_UNCLEANED_BREWS) &&
            d.user_action !== 12,
        );
        setSelectedDevice((prev) => prev || brewEligibleDevice || response[0]);
      }
    } catch {
      setError(true);
    } finally {
      setFetchingDevices(false);
    }
  }, [propSelectedDevice]);

  const fetchBeers = useCallback(async () => {
    if (!userInfo?.user?.id) return;
    setFetchingBeers(true);
    try {
      const response = await recipeService.getBeers(userInfo.user.id);
      setBeers(response || []);
      if (propSelectedRecipe) {
        const matched = response.find(
          (b) => b.id === propSelectedRecipe.beer_id,
        );
        if (matched) {
          setSelectedBeer(matched);
          fetchRecipes(propSelectedRecipe.beer_id);
        }
      }
    } catch {
      setError(true);
    } finally {
      setFetchingBeers(false);
    }
  }, [userInfo?.user?.id, propSelectedRecipe, fetchRecipes]);

  // Load initial data when modal opens
  useEffect(() => {
    if (show) {
      setError(false);
      setErrorMessage("");
      setSubmitted(false);
      setIsSaving(false);
      setConfirmSkipWarning(false);
      fetchDevices();
      fetchBeers();
      checkBrewEligibility();
    } else {
      setSelectedDevice(null);
      setSelectedRecipe(null);
      setSelectedBeer(null);
      setReadinessInfo(null);
      setSkipCleaning(false);
      setConfirmSkipWarning(false);
      setSubmitted(false);
    }
  }, [show, fetchDevices, fetchBeers, checkBrewEligibility]);

  // Fetch device brew readiness whenever selectedDevice or selectedRecipe changes
  useEffect(() => {
    let isCancelled = false;
    const fetchReadiness = async () => {
      if (!selectedDevice) {
        setReadinessInfo(null);
        setSkipCleaning(false);
        return;
      }
      try {
        const readiness = await deviceService.getDeviceBrewReadiness(
          selectedDevice.uuid,
          selectedRecipe?.id,
        );
        if (!isCancelled) {
          setReadinessInfo(readiness);
          if (readiness?.needs_cleaning) {
            setSkipCleaning(readiness.can_skip_cleaning);
          } else {
            setSkipCleaning(false);
          }
        }
      } catch {
        if (!isCancelled) {
          setReadinessInfo(null);
          setSkipCleaning(false);
        }
      }
    };

    fetchReadiness();
    return () => {
      isCancelled = true;
    };
  }, [selectedDevice, selectedRecipe]);

  const startBrew = async () => {
    setSubmitted(true);

    if (!selectedDevice) {
      return;
    }

    // Check CIP blocker
    if (readinessInfo?.needs_cleaning && !readinessInfo?.can_skip_cleaning) {
      setError(true);
      setErrorMessage(
        readinessInfo.skip_cleaning_block_reason ||
          "Device requires a full CIP cleaning cycle before brewing.",
      );
      return;
    }

    // Check high risk confirmation
    if (
      readinessInfo?.needs_cleaning &&
      readinessInfo?.compatibility?.risk_level === "high" &&
      !confirmSkipWarning
    ) {
      setError(true);
      setErrorMessage(
        "Please acknowledge the color/flavor carryover warning before starting the brew.",
      );
      return;
    }

    let data = {};

    if (activeTab === 1) {
      if (isBrewable(selectedRecipe)) {
        data = {
          minibrew_uuid: selectedDevice.uuid,
          session_type: SESSION_TYPES.SESSION_TYPE_BREW_FERM,
          beer_recipe_id: selectedRecipe.id,
        };
      } else {
        return;
      }
    } else if (!enterManually) {
      if (!brewticket) return;
      data = {
        minibrew_uuid: selectedDevice.uuid,
        session_type: SESSION_TYPES.SESSION_TYPE_BREW_FERM,
        ticket_id: brewticket.uuid,
      };
    } else {
      if (!manualCode) return;
      data = {
        minibrew_uuid: selectedDevice.uuid,
        session_type: SESSION_TYPES.SESSION_TYPE_BREW_FERM,
        ticket_id: manualCode,
      };
    }

    if (readinessInfo?.needs_cleaning && skipCleaning) {
      data.skip_cleaning = true;
    }

    setIsSaving(true);
    setError(false);
    setErrorMessage("");

    try {
      const response = await sessionService.startSession(data);
      if (response && response.error <= 0) {
        throw new Error(response.message || "Failed to start session.");
      } else {
        handleClose();
        dispatch(push(`/brewery/monitor/${response.id}`));
      }
    } catch (err) {
      let message = "Failed to start session.";
      if (err && typeof err.then === "function") {
        try {
          const body = await err;
          message =
            (body.minibrew_uuid && body.minibrew_uuid[0]) ||
            body.detail ||
            body.non_field_errors?.[0] ||
            "Failed to start session.";
        } catch {
          message = "Failed to start session.";
        }
      } else if (typeof err === "string") {
        message = err;
      } else if (err?.message) {
        message = err.message;
      }
      setErrorMessage(message);
      setError(true);
      setIsSaving(false);
    }
  };

  const startCleaning = async () => {
    if (!selectedDevice) return;
    setIsSaving(true);
    setError(false);
    setErrorMessage("");

    try {
      const response = await sessionService.startCleaningSession(
        selectedDevice.uuid,
      );
      if (response && response.error <= 0) {
        throw new Error(
          response.message || "Failed to start cleaning session.",
        );
      } else {
        handleClose();
        if (response && response.id) {
          dispatch(push(`/brewery/monitor/${response.id}`));
        } else if (response && response.session) {
          dispatch(push(`/brewery/monitor/${response.session}`));
        } else {
          dispatch(push("/brewery"));
        }
      }
    } catch (err) {
      let message = "Failed to start cleaning session.";
      if (err && typeof err.then === "function") {
        try {
          const body = await err;
          message =
            (body.minibrew_uuid && body.minibrew_uuid[0]) ||
            body.detail ||
            body.non_field_errors?.[0] ||
            "Failed to start cleaning session.";
        } catch {
          message = "Failed to start cleaning session.";
        }
      } else if (typeof err === "string") {
        message = err;
      } else if (err?.message) {
        message = err.message;
      }
      setErrorMessage(message);
      setError(true);
      setIsSaving(false);
    }
  };

  const navigateToEditRecipe = useCallback(() => {
    if (selectedRecipe) {
      dispatch(
        push(
          routes.editRecipeWithValidate(
            selectedRecipe.beer_id,
            selectedRecipe.id,
          ),
        ),
      );
    }
  }, [dispatch, selectedRecipe]);

  const navigateToRecipeDetail = useCallback(() => {
    handleClose();
    if (selectedRecipe) {
      dispatch(
        push(
          routes.recipeDetailsWithParams(
            selectedRecipe.beer_id,
            selectedRecipe.version_name,
          ),
        ),
      );
    }
  }, [dispatch, handleClose, selectedRecipe]);

  const handleScan = useCallback((data) => {
    if (data) {
      const hashedId = data.split("/")[4];
      recipeService
        .getBrewpackByQR(hashedId)
        .then((res) => {
          setBrewticket(res);
          setScanState(scanStates.success);
        })
        .catch(() => {
          setScanState(scanStates.error);
        });
    }
  }, []);

  const handleScanError = useCallback(() => {
    setScanState(scanStates.error);
  }, []);

  const resetScan = useCallback(() => {
    setScanState(scanStates.scanning);
    setBrewticket(null);
  }, []);

  const updateManualCode = useCallback((e) => {
    setManualCode(e.target.value);
  }, []);

  const toggleEnterManual = useCallback(() => {
    setEnterManually((prev) => !prev);
  }, []);

  const sortedRecipes = [...recipes].sort((a, b) => {
    if (a.beer_name < b.beer_name) return 1;
    if (a.beer_name > b.beer_name) return -1;
    if (parseInt(a.version_name, 10) < parseInt(b.version_name, 10)) return 1;
    if (parseInt(a.version_name, 10) > parseInt(b.version_name, 10)) return -1;
    return 0;
  });

  const showRecipesField = selectedRecipe && !fetchingRecipes;
  const showBeersField =
    (selectedBeer && !fetchingBeers) || !propSelectedRecipe;
  const showDevicesField =
    (selectedDevice && !fetchingDevices) || !propSelectedDevice;

  const isHardBlockedByCIP =
    readinessInfo?.needs_cleaning && !readinessInfo?.can_skip_cleaning;
  const hasCompatibilityWarnings = Boolean(
    readinessInfo?.compatibility?.warnings?.length,
  );
  const isHighRisk = readinessInfo?.compatibility?.risk_level === "high";

  const getDeviceLabel = useCallback((option) => {
    if (!option) return "";
    const name = option.custom_name || option.uuid;
    const needsCleaning =
      option.consecutive_uncleaned_brews >= MAX_CONSECUTIVE_UNCLEANED_BREWS ||
      option.user_action === 12;
    return `${name}${needsCleaning ? " (Needs Cleaning)" : ""}`;
  }, []);

  return (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ textAlign: "center" }}>
          START NEW BREW
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          id="brew-modal-tabs"
          style={{ color: "black" }}
          activeKey={activeTab}
          onSelect={(key) => {
            setActiveTab(key);
            setError(false);
          }}
        >
          <Tab title="CUSTOM" eventKey={1}>
            {!brewEligible && (
              <Alert bsStyle="warning" style={{ margin: "15px" }}>
                <strong>Starter plan limit reached.</strong> You can brew one
                custom recipe every 8 weeks.
                {nextBrewDate && (
                  <span>
                    {" "}
                    Your next custom brew is available on{" "}
                    <strong>
                      {new Date(nextBrewDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </strong>
                    .
                  </span>
                )}
              </Alert>
            )}
            <Form>
              <FormGroup controlId="DeviceSelect">
                <Col md={12}>
                  <ControlLabel>Device</ControlLabel>
                  {showDevicesField && (
                    <Typeahead
                      id="device-select-custom"
                      clearButton
                      filterBy={(option, state) => {
                        if (!state.text) return true;
                        if (
                          selectedDevice &&
                          (state.text === getDeviceLabel(selectedDevice) ||
                            state.text === selectedDevice.custom_name ||
                            state.text === selectedDevice.uuid)
                        ) {
                          return true;
                        }
                        const query = state.text.toLowerCase();
                        const name = (option.custom_name || "").toLowerCase();
                        const uuid = (option.uuid || "").toLowerCase();
                        return name.includes(query) || uuid.includes(query);
                      }}
                      onChange={(selected) => {
                        setSelectedDevice(selected[0] || null);
                      }}
                      labelKey={getDeviceLabel}
                      options={devices}
                      selected={selectedDevice ? [selectedDevice] : []}
                      isLoading={fetchingDevices}
                      placeholder="Choose a device"
                      emptyLabel="No available device found"
                    />
                  )}
                  {submitted && !selectedDevice && (
                    <HelpBlock style={{ color: "red" }}>
                      Field is required
                    </HelpBlock>
                  )}
                </Col>
                <div className="clearfix" />
              </FormGroup>

              {/* Cleaning & Readiness Status Banner */}
              {selectedDevice && readinessInfo && (
                <Col
                  md={12}
                  style={{ marginTop: "10px", marginBottom: "15px" }}
                >
                  <div
                    style={{
                      padding: "10px 14px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "6px",
                      border: "1px solid #e9ecef",
                      fontSize: "13px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <strong>Last CIP Cycle:</strong>{" "}
                        {readinessInfo.last_cip_date
                          ? moment(readinessInfo.last_cip_date).format(
                              "MMM D, YYYY HH:mm",
                            )
                          : "Never / Not recorded"}
                      </div>
                      <div>
                        <span
                          className={`label ${
                            readinessInfo.consecutive_uncleaned_brews === 0
                              ? "label-success"
                              : readinessInfo.consecutive_uncleaned_brews === 1
                                ? "label-warning"
                                : "label-danger"
                          }`}
                        >
                          {readinessInfo.consecutive_uncleaned_brews === 0
                            ? "Clean Base"
                            : `${readinessInfo.consecutive_uncleaned_brews} Uncleaned Brew${
                                readinessInfo.consecutive_uncleaned_brews > 1
                                  ? "s"
                                  : ""
                              }`}
                        </span>
                      </div>
                    </div>

                    {isHardBlockedByCIP && (
                      <Alert
                        bsStyle="danger"
                        style={{ marginTop: "10px", marginBottom: "0" }}
                      >
                        <strong>Cleaning Required:</strong>{" "}
                        {readinessInfo.skip_cleaning_block_reason ||
                          `Maximum consecutive brews reached (${MAX_CONSECUTIVE_UNCLEANED_BREWS}). A full cleaning cycle (CIP) is required before brewing.`}
                      </Alert>
                    )}

                    {readinessInfo.needs_cleaning &&
                      readinessInfo.can_skip_cleaning && (
                        <div
                          style={{
                            marginTop: "10px",
                            padding: "8px 10px",
                            backgroundColor: "#fff3cd",
                            borderRadius: "4px",
                            color: "#856404",
                            border: "1px solid #ffeeba",
                          }}
                        >
                          <div>
                            <strong>
                              ⚡ Back-to-Back Brew (Skip Base CIP):
                            </strong>{" "}
                            The built-in pre-mash hot rinse will flush the lines
                            before mashing starts.
                          </div>
                          {hasCompatibilityWarnings && (
                            <div style={{ marginTop: "12px" }}>
                              {readinessInfo.compatibility.warnings.map(
                                (warn, idx) => {
                                  const colonIdx = warn.indexOf(": ");
                                  const title =
                                    colonIdx !== -1
                                      ? warn.substring(0, colonIdx)
                                      : "Warning";
                                  const description =
                                    colonIdx !== -1
                                      ? warn.substring(colonIdx + 2)
                                      : warn;

                                  return (
                                    <div
                                      key={idx}
                                      style={{
                                        marginTop: idx > 0 ? "10px" : "4px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontFamily: "Gotham-Bold",
                                          fontSize: "13px",
                                        }}
                                      >
                                        ⚠️ {title}:
                                      </div>
                                      <div
                                        style={{
                                          marginTop: "4px",
                                          fontFamily: "Gotham-Book",
                                          fontWeight: "normal",
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {description}
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                              {isHighRisk && (
                                <Checkbox
                                  checked={confirmSkipWarning}
                                  onChange={(e) =>
                                    setConfirmSkipWarning(e.target.checked)
                                  }
                                  style={{ marginTop: "12px" }}
                                >
                                  <span style={{ fontFamily: "Gotham-Book" }}>
                                    I understand and accept the risk of
                                    color/flavor carryover from the previous
                                    brew.
                                  </span>
                                </Checkbox>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </Col>
              )}

              {!isHardBlockedByCIP && (
                <React.Fragment>
                  <FormGroup controlId="BeerSelect">
                    <Col md={8}>
                      <ControlLabel>Beers</ControlLabel>
                      {showBeersField && (
                        <Typeahead
                          id="beer-select"
                          onChange={(selected) => {
                            if (selected[0]) {
                              setSelectedRecipe(null);
                              setSelectedBeer(selected[0]);
                              setRecipeError(false);
                              fetchRecipes(selected[0].id);
                            }
                          }}
                          labelKey={(option) => `${option.name}`}
                          options={beers}
                          selected={selectedBeer ? [selectedBeer] : []}
                          isLoading={fetchingBeers}
                          placeholder="Choose a beer"
                          emptyLabel="No beers found"
                        />
                      )}
                      {submitted && !selectedBeer && (
                        <HelpBlock style={{ color: "red" }}>
                          Field is required
                        </HelpBlock>
                      )}
                    </Col>
                  </FormGroup>
                  <FormGroup controlId="VersionSelect">
                    <Col md={4}>
                      <ControlLabel>Version</ControlLabel>
                      {showRecipesField && (
                        <Typeahead
                          id="version-select"
                          ref={typeaheadVersionsRef}
                          disabled={sortedRecipes.length === 0}
                          onChange={(selected) => {
                            if (selected && selected[0]) {
                              setSelectedRecipe(selected[0]);
                            }
                          }}
                          labelKey={(option) =>
                            `Version ${option.version_name}`
                          }
                          options={sortedRecipes}
                          selected={selectedRecipe ? [selectedRecipe] : []}
                          isLoading={fetchingRecipes}
                          placeholder="Choose a version"
                          emptyLabel="No version found"
                        />
                      )}
                      {submitted && !selectedRecipe && (
                        <HelpBlock style={{ color: "red" }}>
                          Field is required
                        </HelpBlock>
                      )}
                    </Col>
                  </FormGroup>
                </React.Fragment>
              )}
            </Form>
            <div className="clearfix" />
            <div
              style={{
                marginTop: "20px",
                padding: "0px 15px",
              }}
            >
              {error && (
                <Alert bsStyle="danger">
                  <span className="ion-alert-circled">
                    {" "}
                    {errorMessage ||
                      "Something went wrong please try again later"}
                  </span>
                </Alert>
              )}
              {!isHardBlockedByCIP && recipeError && (
                <Alert bsStyle="danger">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "left",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <span
                        className="ion-android-alert"
                        style={{ marginRight: "10px", fontSize: "24px" }}
                      />
                    </div>
                    No recipes found
                  </div>
                </Alert>
              )}
              {!isHardBlockedByCIP &&
                selectedRecipe &&
                !selectedRecipe.brewable &&
                selectedRecipe.times_brewed === 0 && (
                  <Alert bsStyle="danger">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <span
                          className="ion-android-alert"
                          style={{ marginRight: "10px", fontSize: "24px" }}
                        />
                      </div>
                      <div>
                        Your recipe cannot be brewed because it does not meet
                        the requirements of the MiniBrew system. You can see why
                        by going to the Edit page for this beer.
                      </div>
                      <div>
                        <Button
                          style={{ float: "right" }}
                          onClick={navigateToEditRecipe}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                    <div className="clearfix" />
                  </Alert>
                )}

              {!isHardBlockedByCIP &&
                selectedRecipe &&
                !selectedRecipe.brewable &&
                selectedRecipe.times_brewed > 0 && (
                  <Alert bsStyle="danger">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <span
                          className="ion-android-alert"
                          style={{ marginRight: "10px", fontSize: "24px" }}
                        />
                      </div>
                      <div>
                        Your recipe cannot be brewed because it no longer meets
                        the requirements of the MiniBrew system. Visit the
                        Recipe Detail page to see which versions can be brewed.
                        Create a new version of any “unbrewable” recipe to see
                        what you need to change.
                      </div>
                      <div>
                        <Button onClick={navigateToRecipeDetail}>
                          Recipe Detail
                        </Button>
                      </div>
                    </div>
                    <div className="clearfix" />
                  </Alert>
                )}
            </div>
          </Tab>
          <Tab title="BREWPACK" eventKey={2}>
            <Form>
              <FormGroup controlId="DeviceSelectBrewpack">
                <ControlLabel>Device</ControlLabel>
                {showDevicesField && (
                  <Typeahead
                    id="device-select-brewpack"
                    clearButton
                    filterBy={(option, state) => {
                      if (!state.text) return true;
                      if (
                        selectedDevice &&
                        (state.text === getDeviceLabel(selectedDevice) ||
                          state.text === selectedDevice.custom_name ||
                          state.text === selectedDevice.uuid)
                      ) {
                        return true;
                      }
                      const query = state.text.toLowerCase();
                      const name = (option.custom_name || "").toLowerCase();
                      const uuid = (option.uuid || "").toLowerCase();
                      return name.includes(query) || uuid.includes(query);
                    }}
                    onChange={(selected) => {
                      setSelectedDevice(selected[0] || null);
                    }}
                    labelKey={getDeviceLabel}
                    options={devices}
                    selected={selectedDevice ? [selectedDevice] : []}
                    isLoading={fetchingDevices}
                    placeholder="Choose a device"
                    emptyLabel="No available device found"
                  />
                )}
                {submitted && !selectedDevice && (
                  <HelpBlock style={{ color: "red" }}>
                    Field is required
                  </HelpBlock>
                )}
                <div className="clearfix" />
              </FormGroup>

              {/* Cleaning & Readiness Status Banner */}
              {selectedDevice && readinessInfo && (
                <Col
                  md={12}
                  style={{ marginTop: "10px", marginBottom: "15px" }}
                >
                  <div
                    style={{
                      padding: "10px 14px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "6px",
                      border: "1px solid #e9ecef",
                      fontSize: "13px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <strong>Last CIP Cycle:</strong>{" "}
                        {readinessInfo.last_cip_date
                          ? moment(readinessInfo.last_cip_date).format(
                              "MMM D, YYYY HH:mm",
                            )
                          : "Never / Not recorded"}
                      </div>
                      <div>
                        <span
                          className={`label ${
                            readinessInfo.consecutive_uncleaned_brews === 0
                              ? "label-success"
                              : readinessInfo.consecutive_uncleaned_brews === 1
                                ? "label-warning"
                                : "label-danger"
                          }`}
                        >
                          {readinessInfo.consecutive_uncleaned_brews === 0
                            ? "Clean Base"
                            : `${readinessInfo.consecutive_uncleaned_brews} Uncleaned Brew${
                                readinessInfo.consecutive_uncleaned_brews > 1
                                  ? "s"
                                  : ""
                              }`}
                        </span>
                      </div>
                    </div>

                    {isHardBlockedByCIP && (
                      <Alert
                        bsStyle="danger"
                        style={{ marginTop: "10px", marginBottom: "0" }}
                      >
                        <strong>Cleaning Required:</strong>{" "}
                        {readinessInfo.skip_cleaning_block_reason ||
                          `Maximum consecutive brews reached (${MAX_CONSECUTIVE_UNCLEANED_BREWS}). A full cleaning cycle (CIP) is required before brewing.`}
                      </Alert>
                    )}
                  </div>
                </Col>
              )}

              {!isHardBlockedByCIP && (
                <React.Fragment>
                  <ButtonGroup style={{ marginBottom: 10 }}>
                    <Button
                      disabled={!enterManually}
                      onClick={toggleEnterManual}
                      style={{ marginRight: 0 }}
                    >
                      Scan
                    </Button>
                    <Button
                      disabled={enterManually}
                      onClick={toggleEnterManual}
                    >
                      Type
                    </Button>
                  </ButtonGroup>

                  {activeTab === 2 && (
                    <>
                      {scanState === scanStates.scanning && !enterManually && (
                        <QrReader
                          delay={300}
                          onError={handleScanError}
                          onScan={handleScan}
                          style={{ width: "100%" }}
                        />
                      )}
                      {enterManually && (
                        <FormControl
                          type="text"
                          placeholder="Brewpack code"
                          value={manualCode}
                          onChange={updateManualCode}
                        />
                      )}
                    </>
                  )}
                  {scanState === scanStates.success &&
                    !enterManually &&
                    brewticket && (
                      <div className="flex flex-row">
                        <div>
                          <img
                            src={brewticket.beer_img}
                            alt={brewticket.beer_name}
                            style={{ width: 80, height: 80, borderRadius: 6 }}
                          />
                        </div>
                        <div
                          className="flex flex-column"
                          style={{ marginLeft: 20 }}
                        >
                          <div>{brewticket.beer_name}</div>
                          <div className="font-book">
                            {brewticket.brewery_name}
                          </div>
                        </div>
                      </div>
                    )}
                  {scanState === scanStates.error && (
                    <Alert bsStyle="danger">Invalid QR code</Alert>
                  )}
                </React.Fragment>
              )}
              {error && <Alert bsStyle="danger">Invalid code</Alert>}
            </Form>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        {isHardBlockedByCIP ? (
          <React.Fragment>
            <Button onClick={handleClose}>Close</Button>
            <ButtonWithLoad
              onClick={startCleaning}
              style={{
                backgroundColor: "#FFC000",
                borderColor: "#FFC000",
                color: "#000",
                fontWeight: "bold",
                borderRadius: "20px",
                padding: "8px 24px",
              }}
              isLoading={isSaving}
            >
              START CLEANING
            </ButtonWithLoad>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Button onClick={handleClose}>Close</Button>
            {activeTab === 2 && scanState !== scanStates.scanning && (
              <Button onClick={resetScan}>Rescan</Button>
            )}
            <ButtonWithLoad
              onClick={startBrew}
              bsStyle="primary"
              disabled={
                (isHighRisk && !confirmSkipWarning) ||
                (selectedRecipe &&
                  !selectedRecipe.brewable &&
                  userInfo?.user?.access_type !== ACCESS_TYPES.BP_RESEARCHER) ||
                (activeTab === 1 && !brewEligible)
              }
              isLoading={isSaving}
            >
              Start new brew
            </ButtonWithLoad>
          </React.Fragment>
        )}
      </Modal.Footer>
    </Modal>
  );
};

BrewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  selectedDevice: PropTypes.string,
  selectedRecipe: PropTypes.object,
};

export default BrewModal;
